<template>
  <div
    class="bulk-embed-container"
    :class="{ 'bulk-embed-fixed': isSlidingModalOpen }"
  >
    <button class="close-icon" @click="goToCampaignManager(true)">
      <CloseIcon />
    </button>
    <div class="bulk-header">
      <h1 class="bulk-title">{{ $t("create.bulk.title") }}</h1>
      <div class="header-button">
        <div class="last-saved">
          {{ $t("create.bulk.lastSave.lastSaved") }} {{ lastSavedTime }}
        </div>
        <button
          v-if="!isPopup && !isBubble"
          class="bulk-action-button"
          @click="openEmbedCodeModal"
        >
          <CodeIcon />{{ $t("create.bulk.headerButtons.embedCode") }}
        </button>
        <button
          class="bulk-action-button"
          @click="openElementsSlidingModal(0, true)"
        >
          <PlayIcon />{{ $t("create.bulk.headerButtons.settings") }}
        </button>
        <button class="bulk-action-button create-feed" @click="createFeed">
          <PlusIcon />
          {{ $t("create.bulk.createFeed") }}
        </button>
      </div>
    </div>
    <div class="feed-nav" :class="{ 'feed-nav-flex-end': !productsCount }">
      <div v-if="productsCount" class="nav-buttons">
        <button
          class="feed-nav-button"
          :class="{ active: activeFeedType === 'general' }"
          @click="setActiveFeedType('general')"
        >
          <GeneralFeeds />
          <p>{{ $t("create.bulk.feedNav.general") }}</p>
          <tippy arrow="true" placement="right" a11y="false" interactive="true">
            <template #trigger class="tooltip-template">
              <TooltipIcon
                class="tooltip-template"
                :content="$t('create.bulk.feedNav.generalTooltip')"
              />
            </template>
            <div class="tooltip-content">
              <p>
                {{ $t("create.bulk.feedNav.generalTooltip") }}
              </p>
              <img
                src="@/assets/generic-feed-compress.png"
                alt="general-feed-image"
                class="generic-feed-image"
              />
            </div>
          </tippy>
        </button>
        <button
          class="feed-nav-button"
          :class="{ active: activeFeedType === 'product' }"
          @click="setActiveFeedType('product')"
        >
          <ProductFeeds />
          <p>{{ $t("create.bulk.feedNav.product") }}</p>
          <TooltipIcon
            v-tippy="{ placement: 'right', arrow: true }"
            :content="$t('create.bulk.feedNav.productTooltip')"
          />
        </button>
      </div>
      <div class="search-filter">
        <AutocompletedSearch
          class="products-search"
          :items="filteredFeedList"
          :type="'feed'"
          :is-for-feeds="true"
          @search="onAutocompletedOptionSelected"
        />
        <div
          v-if="hasLanguageSettings || hasAnyFeedTag"
          class="filter-button"
          :class="{ 'selected-filter-button': showFilterModal }"
          @click="openFilterModal"
        >
          <div class="filter-icon">
            <FilterIcon class="filter-svg" />
          </div>
          <p class="filter-by">
            {{ $t("campaignManager.filter.filters") }}
          </p>
        </div>
      </div>
    </div>
    <div
      class="feed-manager-header"
      :class="{ 'localization-feed-manager-header': hasLanguageSettings }"
      :style="{ gridTemplateColumns: gridCols }"
    >
      <div class="list-element" @click="sortFeedList('feedName')">
        <p class="list-title">
          {{ $t("create.bulk.headerElements.names")
          }}{{ filteredFeedList.length }}
        </p>
        <div class="sorting-arrows">
          <SortArrow
            :class="{
              active: sortBy === 'feedName' && sortDirection === 'asc',
            }"
          />
          <SortArrow
            class="reverse-arrow"
            :class="{
              active: sortBy === 'feedName' && sortDirection === 'desc',
            }"
          />
        </div>
      </div>
      <div v-if="hasTagsColumn" class="list-element">
        <p>Tags</p>
      </div>
      <div
        v-if="hasLanguageSettings"
        class="list-element"
        @click="sortFeedList('locales')"
      >
        <p>Locales</p>
        <div class="sorting-arrows">
          <SortArrow
            :class="{
              active: sortBy === 'locales' && sortDirection === 'asc',
            }"
          />
          <SortArrow
            class="reverse-arrow"
            :class="{
              active: sortBy === 'locales' && sortDirection === 'desc',
            }"
          />
        </div>
      </div>
      <div class="list-element" @click="sortFeedList('modificationDate')">
        <p class="list-title">
          {{ $t("create.bulk.headerElements.modified") }}
        </p>
        <div class="sorting-arrows">
          <SortArrow
            :class="{
              active: sortBy === 'modificationDate' && sortDirection === 'asc',
            }"
          />
          <SortArrow
            class="reverse-arrow"
            :class="{
              active: sortBy === 'modificationDate' && sortDirection === 'desc',
            }"
          />
        </div>
      </div>
      <div class="list-element" @click="sortFeedList('videos')">
        <p class="list-title">
          {{ $t("create.bulk.headerElements.videos") }}
        </p>
        <div class="sorting-arrows">
          <SortArrow
            :class="{ active: sortBy === 'videos' && sortDirection === 'asc' }"
          />
          <SortArrow
            class="reverse-arrow"
            :class="{ active: sortBy === 'videos' && sortDirection === 'desc' }"
          />
        </div>
      </div>
      <div class="list-element" @click="sortFeedList('segmentation')">
        <p class="list-title">
          {{ $t("create.bulk.headerElements.pages") }}
        </p>
        <div class="sorting-arrows">
          <SortArrow
            :class="{
              active: sortBy === 'segmentation' && sortDirection === 'asc',
            }"
          />
          <SortArrow
            class="reverse-arrow"
            :class="{
              active: sortBy === 'segmentation' && sortDirection === 'desc',
            }"
          />
        </div>
      </div>
      <div class="list-element">
        <p class="list-title">
          {{ "" }}
        </p>
      </div>
    </div>
    <div class="feed-cards-container">
      <draggable
        :value="filteredFeedList"
        item-key="originalFeedIndex"
        :disabled="!allowFeedReorder"
        @end="onFeedDragEnd"
      >
        <div
          v-for="(feed, index) in filteredFeedList"
          :key="feed.originalFeedIndex"
          :ref="`feed-${feed.originalFeedIndex}`"
          class="feed-card"
          :class="[
            { 'edited-feed': feed.originalFeedIndex === lastEditedFeedIndex },
            { 'localization-feed-card': hasLanguageSettings },
          ]"
          :style="{ gridTemplateColumns: gridCols }"
        >
          <div class="feed-name-and-date">
            <input
              v-model="feed.feedName"
              v-tippy="{ placement: 'top', arrow: true }"
              class="feed-name-input"
              :content="feed.feedName"
              @input="onFeedNameInput(feed.originalFeedIndex, feed.feedName)"
            />
            <p v-if="feed.creationDate" class="date">
              {{ $t("campaignManager.creationDate") }}
              {{ formattedDates(feed.creationDate) }}
            </p>
          </div>
          <div v-if="hasTagsColumn" class="feed-info">
            <TagsManager
              :is-feed-manager="true"
              :feed="feed"
              :feed-index="feed.originalFeedIndex"
              :campaign-id="campaign._id"
              :all-feeds="feedList"
            />
          </div>

          <div v-if="hasLanguageSettings" class="feed-info">
            <img
              :src="getFeedLocaleFlag(feed) || globeIcon"
              alt="locale"
              class="flag-icon"
            />
          </div>

          <div class="feed-info">
            <p v-if="feed.modificationDate" class="modification-date">
              {{ formattedDates(feed.modificationDate) }}
            </p>
            <p v-else class="modification-date">
              {{ formattedDates(feed.creationDate) }}
            </p>
          </div>

          <div class="feed-info">
            <p
              class="number-of-videos"
              :class="{ 'there-are-videos': feed.videos.length > 0 }"
            >
              <span class="bold">{{ feed.videos.length }}</span>
              {{
                feed.videos.length === 1
                  ? $t("account.usage.video")
                  : $t("account.usage.videos")
              }}
            </p>
            <button
              class="add-manage"
              @click="openElementsSlidingModal(feed.originalFeedIndex)"
            >
              {{ $t("create.bulk.mainButtons.videos") }}
            </button>
          </div>

          <div class="feed-info">
            <p
              class="number-of-videos"
              :class="{ 'there-are-videos': getAmountOfSegmentation(feed) > 0 }"
            >
              <span class="bold">{{ getAmountOfSegmentation(feed) }}</span>
              {{
                getAmountOfSegmentation(feed) === 1
                  ? $t("account.usage.page")
                  : $t("account.usage.pages")
              }}
            </p>
            <button
              class="add-manage"
              @click="openUrlsSlidingModal(feed.originalFeedIndex)"
            >
              {{ $t("create.bulk.mainButtons.urls") }}
            </button>
          </div>

          <button
            class="option-button"
            @click="openOptionsModal(feed.originalFeedIndex)"
          >
            <img
              src="@/assets/campaign-options-button.png"
              alt="options-button"
            />
          </button>

          <!-- Reorder UI al final de la fila -->
          <div v-if="allowFeedReorder" class="prioritization">
            <img
              class="drag-drop-icon"
              src="@/assets/drag-drop-icon.png"
              alt="drag"
            />
            <input
              type="text"
              :disabled="!allowFeedReorder"
              :value="index + 1"
              @change="changeFeedOrderByInput(index, $event)"
            />
          </div>

          <OptionsFeedModal
            v-if="showOptionsFeedModal[feed.originalFeedIndex]"
            :current-feed-edit-index="feed.originalFeedIndex"
            :current-feed="feed"
            :products-count="productsCount"
            :allow-reorder="allowFeedReorder"
            @clone-feed="handleCloneFeed(feed.originalFeedIndex)"
            @open-delete-feed-modal="
              openDeleteFeedModal(feed.originalFeedIndex)
            "
            @close-options-modal="
              handleCloseOptionsModal(feed.originalFeedIndex)
            "
            @set-generic-feed="handleSetGenericFeed(feed.originalFeedIndex)"
            @unset-generic-feed="handleUnsetGenericFeed(feed.originalFeedIndex)"
            @add-tags-to-feed="addTagsToFeed(feed.originalFeedIndex)"
            @enable-reorder="enableFeedReorder"
            @disable-reorder="finishFeedReorder"
          />
        </div>
      </draggable>
    </div>
    <small-modal
      v-if="showDeleteFeedModal"
      :title="$t('create.bulk.deleteFeedModal.title')"
      class="delete-feed-modal"
      :fit-content="true"
      @close="closeDeleteFeedModal"
    >
      <div class="delete-feed-modal-container">
        <h3 class="subtitle">
          {{ $t("create.bulk.deleteFeedModal.subtitle") }}
        </h3>
        <banner-modal
          :title="$t('deleteCampaign.information')"
          :text="$t('create.bulk.deleteFeedModal.banner')"
        >
        </banner-modal>
        <div class="buttons">
          <button
            class="button-primary --grey"
            type="button"
            @click.prevent="closeDeleteFeedModal"
          >
            {{ $t("deleteCampaign.cancel") }}
          </button>
          <button class="button-primary button-flex" @click="deleteFeed()">
            {{ $t("deleteCampaign.delete") }}
            <DeleteIcon class="delete-icon" />
          </button>
        </div>
      </div>
    </small-modal>
    <transition name="modal-slide">
      <urls-sliding-modal
        v-if="showUrlsModal"
        :current-feed-edit-index="currentFeedEditIndex"
        @close-urls-modal="closeUrlsModal"
      ></urls-sliding-modal>
    </transition>
    <transition name="modal-slide">
      <elements-sliding-modal
        v-if="showElementsModal"
        :current-feed-edit-index="currentFeedEditIndex"
        :campaign-id="campaign._id"
        :is-edit-bulk-format="isEditBulkFormat"
        :is-bulk="true"
        @close-elements-modal="closeElementsModal"
      >
      </elements-sliding-modal>
    </transition>
    <transition name="modal-slide">
      <embed-code-sliding-modal
        v-if="showEmbedCodeModal"
        :campaign="getFullCampaign"
        @close-embed-code-modal="closeEmbedCodeModal"
      ></embed-code-sliding-modal>
    </transition>
    <BulkLocalizationFilter
      v-if="showFilterModal"
      :languages-options="languagesOptions"
      :selected-languages="selectedLanguages"
      :tags-options="feedTagsOptions"
      :selected-tags="selectedTags"
      @filter="onFilter"
      @reset="() => (selectedLanguages = [])"
      @close-filter-modal="showFilterModal = false"
    />
    <AddTagsToFeedModal
      v-if="showFeedTagModal"
      :feed="feedList[currentFeedEditIndex]"
      :feed-index="currentFeedEditIndex"
      :campaign-id="campaign._id"
      :all-feeds="feedList"
      @close="showFeedTagModal = false"
    />
  </div>
</template>

<script>
import CloseIcon from "@/assets/svg/settings/close.svg?inline";
import PlayIcon from "@/assets/svg/small-play-icon.svg?inline";
import CodeIcon from "@/assets/svg/embed-code-icon.svg?inline";
import UrlsSlidingModal from "@/components/create-campaign-steps/UrlsSlidingModal.vue";
import ElementsSlidingModal from "@/components/create-campaign-steps/ElementsSlidingModal.vue";
import SmallModal from "@/components/shared/SmallModal.vue";
import BannerModal from "@/components/shared/BannerModal.vue";
import DeleteIcon from "@/assets/svg/delete-small.svg?inline";
import { mapState, mapGetters } from "vuex";
import EmbedCodeSlidingModal from "@/components/create-campaign-steps/EmbedCodeSlidingModal.vue";
import GeneralFeeds from "@/assets/svg/general-feeds.svg?inline";
import ProductFeeds from "@/assets/svg/product-feeds.svg?inline";
import TooltipIcon from "@/assets/svg/tooltip-icon.svg?inline";
import PlusIcon from "@/assets/svg/plus-icon.svg?inline";
import SortArrow from "@/assets/svg/sort-arrow.svg?inline";
import OptionsFeedModal from "@/components/create-campaign-steps/OptionsFeedModal.vue";
import AutocompletedSearch from "@/components/shared/AutocompletedSearch.vue";
import GlobeIcon from "@/assets/globe-icon.png";
import FilterIcon from "@/assets/svg/filter-icon.svg?inline";
import BulkLocalizationFilter from "@/components/create-campaign-steps/BulkLocalizationFilter.vue";
import ReorderIcon from "@/assets/svg/small-reorder-icon.svg?inline";
import Draggable from "vuedraggable";
import AddTagsToFeedModal from "@/components/create-campaign-steps/AddTagsToFeedModal.vue";
import TagsManager from "@/components/campaign-manager/TagsManager.vue";
import { tagMixin } from "@/utils/add-bulk-tag.js";
import enums from "../../enums";
import { locales } from "@/utils/locales";

const { FormatType, DeviceType } = enums;

export default {
  components: {
    CloseIcon,
    PlayIcon,
    CodeIcon,
    UrlsSlidingModal,
    SmallModal,
    BannerModal,
    DeleteIcon,
    ElementsSlidingModal,
    EmbedCodeSlidingModal,
    OptionsFeedModal,
    GeneralFeeds,
    ProductFeeds,
    TooltipIcon,
    PlusIcon,
    SortArrow,
    AutocompletedSearch,
    FilterIcon,
    BulkLocalizationFilter,
    Draggable,
    ReorderIcon,
    AddTagsToFeedModal,
    TagsManager,
  },
  mixins: [tagMixin],
  props: {
    isEditMode: { type: Boolean, default: true },
  },
  data() {
    return {
      showUrlsModal: false,
      showDeleteFeedModal: false,
      showElementsModal: false,
      currentFeedEditIndex: null,
      showEmbedCodeModal: false,
      isEditBulkFormat: false,
      feedIndexToDelete: null,
      wasEmbedCodeOpened: false,
      copy: "Copy coupon code",
      cart: "Add to cart",
      email: "Email collector",
      link: "Link to page",
      showOptionsFeedModal: [],
      savedScrollPosition: 0,
      lastEditedFeedIndex: null,
      activeFeedType: "general",
      sortBy: null,
      sortDirection: "asc",
      timeout: null,
      locales,
      globeIcon: GlobeIcon,
      showFilterModal: false,
      selectedLanguages: [],
      allowFeedReorder: false,
      showFeedTagModal: false,
      selectedTags: [],
    };
  },
  computed: {
    ...mapState(["site"]),
    ...mapState({
      productsCount: (state) => state.product.productsCount,
    }),
    ...mapGetters({
      siteId: "account/getSiteId",
      campaign: "campaign/getSingleCampObj",
      currentFeedEdit: "campaign/currentFeedEdit",
      feedList: "campaign/feedList",
      defaultStoryFormatObject: "campaign/defaultStoryFormatObject",
      defaultCarouselFormatObject: "campaign/defaultCarouselFormatObject",
      defaultFormatObject: "campaign/defaultFormatObject",
      defaultEmbedObject: "campaign/defaultEmbedFormatObject",
      defaultBubbleProperties: "campaign/defaultBubbleProperties",
      getTags: "tag/getTags",
    }),
    segmentationAmount() {
      return this.feedList.reduce(
        (total, feed) => total + this.getAmountOfSegmentation(feed),
        0
      );
    },
    videosAmount() {
      return this.feedList.reduce(
        (total, feed) => total + feed.videos.length,
        0
      );
    },
    getFullCampaign() {
      return {
        ...this.campaign,
        bulk: this.feedList,
      };
    },
    isSlidingModalOpen() {
      return (
        this.showUrlsModal || this.showElementsModal || this.showEmbedCodeModal
      );
    },
    lastSavedTime() {
      if (!this?.campaign?.modificationDate) {
        return "";
      }

      const modificationDate = new Date(this.campaign.modificationDate);
      const currentDate = new Date();
      const diffInMs = currentDate - modificationDate;
      const diffInSeconds = Math.floor(diffInMs / 1000);

      // Determine the correct prefix
      const isEnglish = this.currentLanguage === "en";
      const prefix = isEnglish
        ? ""
        : `${this.$t("create.bulk.lastSave.languageNeed")} `;

      if (diffInSeconds < 60) {
        return `${prefix}${diffInSeconds} ${this.$t(
          "create.bulk.lastSave.seconds"
        )}`;
      }

      const diffInMinutes = Math.floor(diffInSeconds / 60);
      if (diffInMinutes < 60) {
        return `${prefix}${diffInMinutes} ${this.$t(
          "create.bulk.lastSave.minutes"
        )}`;
      }

      const diffInHours = Math.floor(diffInMinutes / 60);
      if (diffInHours < 24) {
        return `${prefix}${diffInHours} ${this.$t(
          "create.bulk.lastSave.hours"
        )}`;
      }

      const diffInDays = Math.floor(diffInHours / 24);
      if (diffInDays < 7) {
        return `${prefix}${diffInDays} ${this.$t("create.bulk.lastSave.days")}`;
      }

      const diffInWeeks = Math.floor(diffInDays / 7);
      if (diffInDays < 30) {
        return `${prefix}${diffInWeeks} ${this.$t(
          "create.bulk.lastSave.weeks"
        )}`;
      }

      const diffInMonths = Math.floor(diffInDays / 30);
      return `${prefix}${diffInMonths} ${this.$t(
        "create.bulk.lastSave.months"
      )}`;
    },
    currentLanguage() {
      return this.$i18n.locale;
    },
    filteredFeedList() {
      let list = this.feedList.filter((feed) => {
        if (feed.isTemporary) return false;
        if (this.activeFeedType === "product")
          return feed.segmentation?.productId?.length === 1;
        else return feed.segmentation?.productId?.length !== 1;
      });

      if (this.selectedLanguages.length) {
        list = list.filter((feed) => {
          const langKey = this.getFeedLocaleKey(feed);
          return this.selectedLanguages.includes(langKey);
        });
      }

      if (this.selectedTags.length) {
        const selectedIds = this.selectedTags.map((t) => t._id);
        list = list.filter((feed) => {
          const feedTagIds = Array.isArray(feed.tags) ? feed.tags : [];
          return selectedIds.some((id) => feedTagIds.includes(id));
        });
      }

      return list;
    },
    isPopup() {
      return this.campaign?.formats[0]?.formatType === FormatType.popup;
    },
    isBubble() {
      return this.campaign?.formats[0]?.formatType === FormatType.bubble;
    },
    hasLanguageSettings() {
      const ls = this.site.site.languageSettings;
      return ls != null && Object.keys(ls).length > 0;
    },
    languagesOptions() {
      return Object.keys(this.site.site.languageSettings).map((langKey) => {
        const label =
          this.locales.find((l) => l.code === `/${langKey}/`)?.name || langKey;
        return { language: langKey, label };
      });
    },
    gridCols() {
      // Base: name/date
      const cols = ["2.5fr"];
      // Tags
      if (this.hasTagsColumn) cols.push("1.3fr");
      // Locales
      if (this.hasLanguageSettings) cols.push("0.7fr");
      // Modified, Videos, Pages, Options
      cols.push("1fr", "1fr", "1fr", "0.5fr");
      if (this.allowFeedReorder) cols.push("0.5fr");
      return cols.join(" ");
    },
    hasTagsColumn() {
      return this.feedList.some(
        (f) => Array.isArray(f?.tags) && f.tags.length > 0
      );
    },
    feedTagsOptions() {
      return (this.getTags || []).filter((t) => t?.type === "feed");
    },
    hasAnyFeedTag() {
      return (this.feedList || []).some(
        (f) => Array.isArray(f.tags) && f.tags.length > 0
      );
    },
  },
  async created() {
    try {
      const { goal, type, isEditMultipleFeed, feedIndex } = this.$route.query;

      if (this.isEditMode) {
        await this.$store.dispatch("campaign/getSingle", {
          campaignId: this.$route.params.campaignId,
          siteId: this.siteId,
        });
      } else {
        const formats = this.getFormatsObject({ goal, type });
        const tag = await this.addBulkTag();

        await this.$store.dispatch("campaign/createBulkCampaign", {
          siteId: this.siteId,
          formats,
          tags: [tag._id],
        });
      }
      await this.$store.dispatch("tag/getTags", { siteId: this.siteId });
      this.$store.commit("campaign/setFeedList", this.campaign.bulk);

      if (isEditMultipleFeed && feedIndex !== undefined) {
        const index = parseInt(feedIndex, 10);
        if (index >= 0 && index < this.feedList.length) {
          this.lastEditedFeedIndex = index;

          this.$nextTick(() => {
            const feedElement = this.$refs[`feed-${index}`];
            if (feedElement && feedElement[0]) {
              feedElement[0].scrollIntoView({ behavior: "smooth" });
            }
          });

          setTimeout(() => {
            this.lastEditedFeedIndex = null;
          }, 5000);
        }
      }
    } catch (err) {
      this.$notify({
        title: this.$t("shared.toastMessage.error"),
        text: err,
        type: "error",
      });
      throw err;
    }
    await this.$store.dispatch("site/getSite", { siteId: this.siteId });
    this.feedList.forEach(() => {
      this.showOptionsFeedModal.push(false);
    });
    await this.$store.dispatch("product/getProducts", {
      siteId: this.siteId,
      isProductListView: true,
    });
    await this.$store.dispatch("product/getProductsCount", this.siteId);
  },
  methods: {
    openUrlsSlidingModal(index) {
      this.currentFeedEditIndex = index;
      this.$store.commit("campaign/setCurrentFeedEdit", this.feedList[index]);
      this.savedScrollPosition = window.pageYOffset;
      this.showUrlsModal = true;
    },
    openElementsSlidingModal(index, isFormatEdit = false) {
      //This way we know if we are editing Formats or Videos elements
      this.isEditBulkFormat = isFormatEdit;
      this.isEditBulkFormat = isFormatEdit;

      if (this.isEditBulkFormat && this.feedList.length === 0) {
        const tempFeed = {
          feedName: "Temporary Feed",
          segmentation: { productId: ["temp"] },
          videos: [],
          creationDate: new Date().toISOString(),
          modificationDate: new Date().toISOString(),
          isTemporary: true,
        };
        this.$store.dispatch("campaign/createFeed", {
          campaignId: this.campaign._id,
          newFeed: tempFeed,
        });
        index = 0;
      }

      this.currentFeedEditIndex = index;
      this.$store.commit("campaign/setCurrentFeedEdit", this.feedList[index]);
      this.savedScrollPosition = window.pageYOffset;

      // if (this.isEditBulkFormat && !this.feedList[0]?.videos) {
      //   return this.$notify({
      //     title: this.$t("shared.toastMessage.error"),
      //     text: this.$t("shared.toastMessage.addVideosFirstFeed"),
      //     type: "error",
      //   });
      // }

      this.showElementsModal = true;
    },
    openEmbedCodeModal() {
      if (this.campaign?.formats[0]?.formatType === FormatType.embed) {
        if (!this.feedList.length || !this.feedList[0]?.videos?.length) {
          return this.$notify({
            title: this.$t("shared.toastMessage.error"),
            text: this.$t("shared.toastMessage.firstFeedCode"),
            type: "error",
          });
        }
      }
      this.savedScrollPosition = window.pageYOffset;
      this.showEmbedCodeModal = true;
      this.wasEmbedCodeOpened = true;
    },
    closeUrlsModal() {
      this.lastEditedFeedIndex = this.currentFeedEditIndex;
      this.showUrlsModal = false;
      this.scrollToSavedPosition();
      setTimeout(() => {
        this.lastEditedFeedIndex = null;
      }, 5000);
    },
    closeElementsModal() {
      if (!this.isEditBulkFormat) {
        this.lastEditedFeedIndex = this.currentFeedEditIndex;
      }
      this.showElementsModal = false;
      this.scrollToSavedPosition();
      const tempFeedIndex = this.feedList.findIndex((feed) => feed.isTemporary);
      if (tempFeedIndex !== -1) {
        this.$store.commit("campaign/deleteFeed", tempFeedIndex);
      }
      if (!this.isEditBulkFormat) {
        setTimeout(() => {
          this.lastEditedFeedIndex = null;
        }, 5000);
      }
    },
    closeEmbedCodeModal() {
      this.showEmbedCodeModal = false;
      this.scrollToSavedPosition();
    },

    goToCampaignManager(closeBulkView = false) {
      const query = {};

      if (
        !this.isEditMode &&
        !this.wasEmbedCodeOpened &&
        !closeBulkView &&
        this.campaign?.formats[0]?.formatType !== FormatType.bubble &&
        this.campaign?.formats[0]?.formatType !== FormatType.popup
      ) {
        query.showEmbed = true;
      }

      this.$router.push({
        name: "campaignManager",
        params: {
          campaignId: this.campaign._id,
        },
        query,
      });
    },

    async createFeed() {
      this.setActiveFeedType("general");
      const newFeed = {
        feedName: "Feed name " + (this.feedList.length + 1),
        segmentation: {},
        videos: [],
        creationDate: new Date().toISOString(),
        modificationDate: new Date().toISOString(),
      };

      await this.$store.dispatch("campaign/createFeed", {
        campaignId: this.campaign._id,
        newFeed,
      });
      this.showOptionsFeedModal.push(false);
      this.lastEditedFeedIndex = this.feedList.length - 1;

      this.$notify({
        title: this.$t("shared.toastMessage.success"),
        text: this.$t("create.bulk.notification.createFeed"),
        type: "success",
      });

      this.scrollToEndOfPage();

      setTimeout(() => {
        this.lastEditedFeedIndex = null;
      }, 5000);
    },
    onFeedNameInput(index, name) {
      if (this.timeout) {
        clearTimeout(this.timeout);
      }

      this.timeout = setTimeout(() => {
        const updatedFeed = {
          ...this.feedList[index],
          feedName: name,
        };

        this.$store.dispatch("campaign/updateFeed", {
          campaignId: this.campaign._id,
          feedIndex: index,
          feedData: updatedFeed,
        });

        this.timeout = null;
      }, 500);
    },
    openDeleteFeedModal(index) {
      this.feedIndexToDelete = index;
      this.showDeleteFeedModal = true;
    },
    closeDeleteFeedModal() {
      this.showDeleteFeedModal = false;
    },
    deleteFeed() {
      this.$store.dispatch("campaign/deleteFeed", {
        campaignId: this.campaign._id,
        feedIndex: this.feedIndexToDelete,
      });

      this.showOptionsFeedModal.splice(this.feedIndexToDelete, 1);
      this.closeDeleteFeedModal();
      this.$notify({
        title: this.$t("shared.toastMessage.success"),
        text: this.$t("create.bulk.notification.deleteFeed"),
        type: "success",
      });
    },
    getAmountOfSegmentation(feed) {
      let base;
      if (feed.segmentation.advanced) {
        base = !feed.segmentation.advanced[0]?.filters
          ? 1
          : feed.segmentation.advanced[0].filters.length;
      } else if (!feed.segmentation.url || !feed.segmentation.url.length) {
        base = feed.segmentation.productId
          ? feed.segmentation.productId.length
          : 0;
      } else if (!feed.segmentation.url[0]?.filters) {
        base = feed.segmentation.productId
          ? feed.segmentation.productId.length + 1
          : 1;
      } else {
        base =
          feed.segmentation.url[0].filters.length +
          (feed.segmentation.productId
            ? feed.segmentation.productId.length
            : 0);
      }

      return base;
    },
    getFormatsObject({ goal, type }) {
      switch (goal) {
        case "carousel":
          if (type === "fullScreen")
            return [
              {
                ...this.defaultFormatObject,
                ...this.defaultCarouselFormatObject,
                isFullScreen: true,
                carouselThumbnailAmount: 1,
              },
            ];
          return [
            {
              ...this.defaultFormatObject,
              ...this.defaultCarouselFormatObject,
              isFullScreen: false,
            },
          ];
        case "stories":
          return [
            {
              ...this.defaultFormatObject,
              ...this.defaultStoryFormatObject,
            },
          ];
        case "embed":
          return [
            {
              ...this.defaultFormatObject,
              ...this.defaultEmbedObject,
            },
          ];
        case "bubble":
          return [
            {
              ...this.defaultFormatObject,
              ...this.defaultBubbleProperties,
              deviceType: DeviceType.mobile,
            },
            {
              ...this.defaultFormatObject,
              ...this.defaultBubbleProperties,
              deviceType: DeviceType.desktop,
            },
          ];
        case "fullscreen-bubble":
          return [
            {
              ...this.defaultFormatObject,
              ...this.defaultBubbleProperties,
              isFullScreen: true,
              deviceType: DeviceType.mobile,
            },
            {
              ...this.defaultFormatObject,
              ...this.defaultBubbleProperties,
              isFullScreen: true,
              deviceType: DeviceType.desktop,
            },
          ];
        case "popup":
          return [
            {
              ...this.defaultFormatObject,
              formatType: FormatType.popup,
              deviceType: DeviceType.mobile,
            },
            {
              ...this.defaultFormatObject,
              formatType: FormatType.popup,
              deviceType: DeviceType.desktop,
            },
          ];
      }
    },
    uniqueCTATypes(feed) {
      const uniqueTypes = new Set();
      feed.videos.forEach((video) => {
        if (video.cta) {
          uniqueTypes.add(video.cta.ctaType);
        }
      });
      return Array.from(uniqueTypes);
    },
    openOptionsModal(index) {
      this.currentFeedEditIndex = index;
      this.showOptionsFeedModal = this.showOptionsFeedModal.map(
        (value, i) => i === index
      );
    },
    handleCloseOptionsModal(index) {
      this.$set(this.showOptionsFeedModal, index, false);
    },
    handleCloneFeed(index) {
      const feedToClone = this.feedList[index];

      const clonedFeed = {
        ...feedToClone,
        feedName: `${feedToClone.feedName} (Copy)`,
        creationDate: new Date().toISOString(),
        modificationDate: new Date().toISOString(),
      };

      this.$store.dispatch("campaign/createFeed", {
        campaignId: this.campaign._id,
        newFeed: clonedFeed,
      });

      this.showOptionsFeedModal.push(false);
      this.handleCloseOptionsModal(index);
      this.$notify({
        title: this.$t("shared.toastMessage.success"),
        text: this.$t("create.bulk.notification.cloneFeed"),
        type: "success",
      });
      this.scrollToEndOfPage();
    },
    formattedDates(date) {
      const invoicingDate = date ? new Date(date) : new Date();
      const month = invoicingDate.toLocaleString(this.$i18n.locale, {
        month: "short",
      });
      const day = invoicingDate.getDate(); // Correct way to get the day
      const year = invoicingDate.getFullYear(); // Correct way to get the year

      return `${month} ${day}, ${year}`;
    },
    scrollToEndOfPage() {
      setTimeout(() => {
        window.scrollTo({
          top: document.body.scrollHeight,
          behavior: "smooth",
        });
      }, 100);
    },
    scrollToSavedPosition() {
      setTimeout(() => {
        window.scrollTo({ top: this.savedScrollPosition, behavior: "smooth" });
      }, 100);
    },
    setActiveFeedType(type) {
      this.activeFeedType = type;
    },
    sortFeedList(property) {
      if (this.sortBy === property) {
        this.sortDirection = this.sortDirection === "asc" ? "desc" : "asc";
      } else {
        this.sortBy = property;
        this.sortDirection = "asc";
      }

      this.filteredFeedList.sort((a, b) => {
        let valueA, valueB;

        switch (property) {
          case "feedName":
            valueA = a.feedName.toLowerCase();
            valueB = b.feedName.toLowerCase();
            break;
          case "locales":
            valueA = this.getFeedLocaleKey(a).toLowerCase();
            valueB = this.getFeedLocaleKey(b).toLowerCase();
            break;
          case "modificationDate":
            valueA = a.modificationDate || a.creationDate;
            valueB = b.modificationDate || b.creationDate;
            break;
          case "videos":
            valueA = a.videos.length;
            valueB = b.videos.length;
            break;
          case "segmentation":
            valueA = this.getAmountOfSegmentation(a);
            valueB = this.getAmountOfSegmentation(b);
            break;
          default:
            return 0;
        }

        if (valueA < valueB) {
          return this.sortDirection === "asc" ? -1 : 1;
        }
        if (valueA > valueB) {
          return this.sortDirection === "asc" ? 1 : -1;
        }
        return 0;
      });
    },
    onAutocompletedOptionSelected(item) {
      if (item !== undefined) {
        const selectedFeedIndex = this.filteredFeedList.findIndex(
          (feed) => feed.originalFeedIndex === item.originalFeedIndex
        );
        if (selectedFeedIndex !== -1) {
          this.scrollToFeed(selectedFeedIndex);
        }
      }
    },
    scrollToFeed(originalFeedIndex) {
      const feedElement = this.$refs[`feed-${originalFeedIndex}`];
      if (feedElement && feedElement[0]) {
        feedElement[0].scrollIntoView({ behavior: "smooth", block: "start" });
      }
    },
    extractUrlStrings(feed) {
      const segStrings = [];
      (feed.segmentation.url || []).forEach((item) => {
        if (item.filters) {
          item.filters.forEach((f) => {
            if (f.comparison === "contains") segStrings.push(f.string);
          });
        } else if (item.comparison === "contains") {
          segStrings.push(item.string);
        }
      });
      return segStrings;
    },

    getFeedLocaleKey(feed) {
      const settings = this.site.site.languageSettings || {};

      if (feed.segmentation.advanced?.length) {
        const lang = this.findLanguageInAdvanced(feed.segmentation.advanced);
        return lang || "";
      }

      const langs = feed.segmentation.language || [];
      if (langs.length === 1) return langs[0];
      if (langs.length > 1) return "";

      const segStrings = this.extractUrlStrings(feed);
      const matched = Object.entries(settings)
        .filter(([langKey, setting]) => {
          const settingStrs = setting.filters
            ? setting.filters
                .filter((f) => f.comparison === "contains")
                .map((f) => f.string)
            : setting.comparison === "contains"
            ? [setting.string]
            : [];
          return settingStrs.some((s) => segStrings.includes(s));
        })
        .map(([langKey]) => langKey);
      return matched.length === 1 ? matched[0] : "";
    },

    getFeedLocaleFlag(feed) {
      const langKey = this.getFeedLocaleKey(feed);
      if (!langKey) return null;
      const code = `/${langKey}/`;
      const entry = this.locales.find((l) => l.code === code);
      return entry?.flag || null;
    },
    openFilterModal() {
      this.showFilterModal = true;
    },
    findLanguageInAdvanced(nodes) {
      if (!Array.isArray(nodes)) return null;
      for (const node of nodes) {
        if (node.dataType === "language") {
          return node.string;
        }
        if (Array.isArray(node.filters)) {
          const flat = node.filters.reduce((acc, f) => {
            if (f.filters) return acc.concat(f.filters);
            return acc.concat(f);
          }, []);
          const found = this.findLanguageInAdvanced(flat);
          if (found) return found;
        }
      }
      return null;
    },
    async handleSetGenericFeed(feedIndex) {
      const feed = this.feedList[feedIndex];
      const generalTag = await this.getOrCreateGeneralFeedTag();
      const updatedFeed = {
        ...feed,
        tags: [...new Set([...(feed.tags || []), generalTag._id])],
        isGeneralFeed: true,
      };

      try {
        await this.$store.dispatch("campaign/updateFeed", {
          campaignId: this.campaign._id,
          feedIndex,
          feedData: updatedFeed,
        });

        this.handleCloseOptionsModal(feedIndex);
        this.$notify({
          title: this.$t("shared.toastMessage.success"),
          text: this.$t("create.bulk.notification.setGenericFeed"),
          type: "success",
        });
      } catch (err) {
        this.$notify({
          title: this.$t("shared.toastMessage.error"),
          text: err.message || err,
          type: "error",
        });
      }
    },

    async handleUnsetGenericFeed(feedIndex) {
      const feed = this.feedList[feedIndex];
      const generalTag = this.getTags.find(
        (t) => t?.name === "Generic feed" && t?.type === "feed"
      );

      const updatedFeed = {
        ...feed,
        isGeneralFeed: false,
        tags: generalTag
          ? (feed.tags || []).filter((id) => id !== generalTag._id)
          : feed.tags || [],
        modificationDate: new Date().toISOString(),
      };

      try {
        await this.$store.dispatch("campaign/updateFeed", {
          campaignId: this.campaign._id,
          feedIndex,
          feedData: updatedFeed,
        });

        this.handleCloseOptionsModal(feedIndex);
        this.$notify({
          title: this.$t("shared.toastMessage.success"),
          text: this.$t("create.bulk.notification.unsetGenericFeed"),
          type: "success",
        });
      } catch (err) {
        this.$notify({
          title: this.$t("shared.toastMessage.error"),
          text: err.message || err,
          type: "error",
        });
      }
    },
    enableFeedReorder() {
      this.allowFeedReorder = true;
      this.showOptionsFeedModal = this.showOptionsFeedModal.map(() => false);
    },
    finishFeedReorder() {
      this.allowFeedReorder = false;
      this.showOptionsFeedModal = this.showOptionsFeedModal.map(() => false);
    },
    onFeedDragEnd(evt) {
      if (!this.allowFeedReorder) return;
      const { oldIndex, newIndex } = evt;
      if (oldIndex === newIndex) return;

      const moved = this.filteredFeedList[oldIndex];
      const anchor = this.filteredFeedList[newIndex];

      this.$notify({
        title: this.$t("shared.toastMessage.success"),
        text: this.$t("shared.toastMessage.campaignOrder"),
        type: "success",
      });

      this.reorderByGlobalIndices(
        moved.originalFeedIndex,
        anchor.originalFeedIndex
      );
    },

    changeFeedOrderByInput(oldIndexFiltered, event) {
      if (!this.allowFeedReorder) return;

      let newIndexFiltered = parseInt(event.target.value, 10) - 1;
      const max = this.filteredFeedList.length - 1;
      if (isNaN(newIndexFiltered)) return;
      if (newIndexFiltered < 0) newIndexFiltered = 0;
      if (newIndexFiltered > max) newIndexFiltered = max;
      if (newIndexFiltered === oldIndexFiltered) return;

      const moved = this.filteredFeedList[oldIndexFiltered];
      const anchor = this.filteredFeedList[newIndexFiltered];

      this.$notify({
        title: this.$t("shared.toastMessage.success"),
        text: this.$t("shared.toastMessage.campaignOrder"),
        type: "success",
      });

      this.reorderByGlobalIndices(
        moved.originalFeedIndex,
        anchor.originalFeedIndex
      );

      this.$nextTick(() => this.scrollToFeed(anchor.originalFeedIndex));
    },

    async reorderByGlobalIndices(indexFrom, indexTo) {
      if (indexFrom === indexTo) return;

      try {
        await this.$store.dispatch("campaign/reorderFeeds", {
          campaignId: this.campaign._id,
          indexFrom,
          indexTo,
        });

        // this.$notify({
        //   title: this.$t("shared.toastMessage.success"),
        //   text: this.$t("shared.toastMessage.campaignOrder"),
        //   type: "success",
        // });
      } catch (err) {
        // this.$notify({
        //   title: this.$t("shared.toastMessage.error"),
        //   text: err?.message || err,
        //   type: "error",
        // });
      }
    },
    addTagsToFeed(originalFeedIndex) {
      this.currentFeedEditIndex = originalFeedIndex;
      this.$store.commit(
        "campaign/setCurrentFeedEdit",
        this.feedList[originalFeedIndex]
      );
      this.showFeedTagModal = true;
    },
    async getOrCreateGeneralFeedTag() {
      let tag = this.getTags.find(
        (t) => t?.name === "Generic feed" && t?.type === "feed"
      );
      if (tag) return tag;

      tag = await this.$store.dispatch("tag/createTag", {
        name: "Generic feed",
        type: "feed",
        siteId: this.siteId,
      });

      return tag;
    },
    onFilter(payload) {
      if (Object.prototype.hasOwnProperty.call(payload, "languages")) {
        this.selectedLanguages = payload.languages;
      }
      if (Object.prototype.hasOwnProperty.call(payload, "tags")) {
        this.selectedTags = payload.tags;
      }
    },
  },
};
</script>

<style lang="scss">
.feed-list-header .list-header {
  grid-template-columns: 2.5fr 1fr 1fr 1fr 0.5fr !important;
}
</style>

<style lang="scss" scoped>
.bulk-embed-container {
  padding: 30px 74px;
  position: relative;
}

.bulk-embed-fixed {
  position: fixed;
}

.close-icon {
  top: 10px;
  right: 10px;
  background: none;
  position: fixed;
}

.bulk-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 42px;
}

.bulk-title {
  @include base-font;
  @include font-big;
  font-weight: 700;
}

.header-button {
  display: flex;
  align-items: center;
  gap: 16px;
}

.bulk-action-button {
  padding: 8px 24px;
  height: 35px;
  box-sizing: border-box;
  border: 1px solid $dark-purple;
  border-radius: 4px;
  background-color: white;
  @include base-font;
  @include font-small;
  color: $dark-purple;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;
  svg {
    path {
      fill: $dark-purple;
    }
  }
  &:hover {
    background-color: rgba(226, 124, 252, 0.1);
  }
}

.first-button {
  border-radius: 3px 0px 0px 3px;
  border-right: none;
}

.second-button {
  border-radius: 0 3px 3px 0;
}

.last-saved {
  padding: 8px 12px;
  @include base-font;
  @include font-smaller;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 12px;
  color: $light-grey;
}

.save-icon {
  path {
    fill: white;
  }
}

.feed-list {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}

.left {
  display: flex;
  align-items: center;
  gap: 8px;
}

.feed-title {
  @include base-font;
  @include font-big;
}
.number-feeds {
  @include base-font;
  @include font-normal;
}

.feed-cards-container {
  display: flex;
  flex-direction: column;
}

.feed-card {
  display: grid;
  grid-template-columns: 2.5fr 1fr 1fr 1fr 0.5fr;
  place-items: flex-start;
  align-items: center;
  border-bottom: 1px solid $light-grey;
  padding: 10px 12px;
  box-sizing: content-box;
  position: relative;
  &:first-child {
    border-top: 1px solid $light-grey;
  }
}
.localization-feed-card {
  grid-template-columns: 2.5fr 1fr 1fr 1fr 1fr 0.5fr;
}
.feed-name-input {
  @include base-font;
  @include font-normal;
  border: none;
  background-color: transparent;
  @media (min-width: 1400px) {
    width: 90%;
  }
}

.feed-info {
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  gap: 4px;
}

.delete-feed-card {
  width: 100%;
  text-align: end;
  .delete-icon-card {
    transform: scale(225%);
    margin-right: 8px;
    cursor: pointer;
    path {
      fill: $light-grey;
    }
  }
}

.delete-feed-modal-container {
  width: 450px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
}
.subtitle {
  text-align: center;
  @include font-normal;
  font-weight: 400;
}

.delete-icon {
  transform: scale(175%);
}

.buttons {
  display: flex;
  justify-content: space-evenly;
  width: 100%;
  margin-top: 24px;
}

.button-flex {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.submit-button {
  @include base-font;
  @include font-small;
  font-weight: 500;
  background: none;
  border: none;
}

.--red {
  box-shadow: 2px 2px 15px 0px rgba(235, 0, 0, 0.25);
  &:hover {
    background-color: rgba(235, 0, 0, 0.25);
  }
}

.feed-info-elements {
  display: flex;
  align-items: center;
  gap: 16px;
}

.elements-icons {
  display: flex;
  align-items: center;
  gap: 8px;
}

.cta-icon {
  transform: scale(115%);
  outline: none;
}
.option-button {
  background: transparent;
}

.feed-name-and-date {
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 90%;
}

.date {
  @include font-smallest;
  color: $light-grey;
  padding-left: 2px;
}
.edited-feed {
  background-color: $light-pink;
}
.feed-nav {
  display: flex;
  margin-bottom: 20px;
  align-items: center;
  justify-content: space-between;
}
.feed-nav-flex-end {
  justify-content: flex-end;
}
.nav-buttons {
  display: flex;
}

.feed-nav-button {
  padding: 8px 16px;
  @include base-font;
  @include font-small;
  font-weight: 400;
  cursor: pointer;
  background: none;
  color: $dark-purple;
  display: flex;
  align-items: center;
  border-bottom: 3px solid rgba(196, 196, 196, 1);
  p {
    margin-left: 8px;
    margin-right: 4px;
  }
  &.active {
    border-bottom: 3px solid $dark-purple;
  }
}

.create-feed {
  background-color: $dark-purple;
  color: white;
  svg {
    path {
      fill: white;
    }
  }
  &:hover {
    color: $dark-purple;
    svg {
      path {
        fill: $dark-purple;
      }
    }
  }
}
.tooltip-template {
  margin-top: 3px;
}

.tooltip-content {
  width: fit-content;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
  p {
    font-size: 14px;
    max-width: 300px;
    text-align: left;
  }
}

.number-of-videos {
  @include font-smaller;
  font-weight: 400;
  background-color: #e2e2e2;
  width: fit-content;
  border-radius: 20px;
  padding: 4px 12px;
  span {
    font-weight: 600;
  }
}
.there-are-videos {
  background-color: #d4f8d3;
}
.add-manage {
  @include base-font;
  @include font-smaller;
  color: $dark-purple;
  text-decoration: underline;
  cursor: pointer;
  background: none;
  height: 20px;
}
.bold {
  font-weight: bold;
}

.modification-date {
  @include base-font;
  @include font-smaller;
}

.generic-feed-image {
  max-width: 280px;
}

.feed-manager-header {
  display: grid;
  grid-template-columns: 2.5fr 1fr 1fr 1fr 0.5fr;
  place-items: flex-start;
  align-items: center;
  position: sticky;
  top: 0;
  z-index: 20; 
  display: grid;
  color: #585858;
  background: #ececec;
  border: 1px solid #ececec;
  box-sizing: border-box;
  border-radius: 2px;
  align-items: center;
  text-align: start;
  font-weight: 400;
  font-size: 14px;
  line-height: 17px;
  // height: 100%;
  padding: 14px;
}
.localization-feed-manager-header {
  grid-template-columns: 2.5fr 1fr 1fr 1fr 1fr 0.5fr;
}
.list-element {
  display: flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
}
.sorting-arrows {
  display: flex;
  flex-direction: column;
  gap: 6px;
  transform: scale(60%);
  path {
    fill: $dark-grey;
  }
  .active path {
    fill: $light-purple;
  }
}
.reverse-arrow {
  transform: translateX(-0.9px);
  path {
    transform: rotate(180deg);
    transform-origin: 50% 50%;
  }
}

.flag-icon {
  width: 16px;
  height: 16px;
}
.search-filter {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  width: 45%;
}
.filter-button {
  @include base-font;
  @include font-small;
  display: flex;
  gap: 8px;
  align-items: center;
  border: 1px solid $dark-grey;
  border-radius: 2px;
  padding: 4px 12px;
  height: 30px;
  position: relative;
  box-sizing: border-box;
  &:hover {
    background: #f1f1f6;
    cursor: pointer;
    svg {
      path {
        fill: $dark-grey;
      }
    }
  }
}
.products-search {
  width: 70% !important;
}
.prioritization {
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding-left: 16px;
  border-radius: 6px;
  &:hover {
    background-color: rgba(244, 240, 240, 0.849);
  }
  input {
    font-family: "Yeon Sung", "sans-serif";
    font-size: 28px;
    font-style: normal;
    font-weight: normal;
    text-align: start;
    color: #c4c4c4;
    max-width: 30px;
    border: 0;
    background: transparent;
  }
}
.drag-drop-icon {
  height: 18px;
  margin-right: 10px;
  padding-bottom: 3px;
}
.reorder-button {
  @include base-font;
  @include font-small;
  background: transparent;
  width: 100%;
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 8px;
}
.reorder-icon {
  circle {
    fill: $dark-grey;
  }
}
</style>
